import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

import { ErrorStrings } from 'src/app/constants/form.constants';
import { Alert } from 'src/app/interfaces/alert';
import { ClickTrackerService } from 'src/app/services/click-tracker/click-tracker.service';
import { ClickEventNumbersEnum, TrackingPayloadType } from 'src/app/interfaces/clickEventNumbers';
import {
  AuthManagerService,
  ClaimAccountParams,
} from 'src/app/services/auth-manager/auth-manager.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';

@Component({
  selector: 'app-claim-account',
  templateUrl: './claim-account.component.html',
  styleUrls: ['./claim-account.component.scss'],
})
export class ClaimAccountComponent implements OnInit {
  // region Variables

  public hideConfirmPassword = true;
  public hidePassword = true;
  public loading = false;
  public errorStrings = ErrorStrings;
  public alert: Alert = null;
  public model = {
    password: '',
  };
  public useRebrandedLayout: boolean;

  private _emailAddress: string;
  private _emailVerificationCode: string;
  private isBrowser: boolean;
  private trackingPayload: TrackingPayloadType = {
    event: ClickEventNumbersEnum.ClaimAccountOpened,
    details: '',
  };

  // endregion

  // region Construct

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _authManager: AuthManagerService,
    private _router: Router,
    private clickTrackerService: ClickTrackerService,
    private featureFlagService: FeatureFlagsService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.useRebrandedLayout = this.featureFlagService.isEnabled('useRebrandedLayout');
  }

  // endregion

  // region Initialization

  ngOnInit(): void {
    const queryParams = this._activatedRoute.snapshot.queryParams;

    if (
      !queryParams ||
      (queryParams && !queryParams.emailAddress) ||
      (queryParams && !queryParams.evc)
    ) {
      this.alert = {
        type: 'danger',
        message:
          'An error has occurred, the URL is invalid! Please try to open the claim email again, ' +
          'in case the issue still persist please feel free to contact us.',
      };
    } else {
      this._emailAddress = queryParams.emailAddress;
      this._emailVerificationCode = queryParams.evc;

      if (this.isBrowser) {
        this.trackingPayload.emailAddress = queryParams.emailAddress;
        this.trackingPayload.emailVerificationCode = queryParams.evc;

        this.clickTrackerService.sendPayload(this.trackingPayload);
      }
    }
  }

  // endregion

  // region Public functions

  public claimAccount() {
    this.closeAlert();
    this.loading = true;

    const claimAccountParams: ClaimAccountParams = {
      password: this.model.password,
      emailAddress: this._emailAddress,
      emailVerificationCode: this._emailVerificationCode,
    };

    this._authManager
      .claimAccount(claimAccountParams)
      .subscribe(
        () => {
          this.alert = {
            type: 'success',
            message: `Your account was activated, you'll get redirected in a few seconds!`,
          };

          setTimeout(() => {
            this._router.navigate(['/login']);
          }, 2000);
        },
        (err) => {
          if (err instanceof HttpErrorResponse && err.status === 400) {
            this.alert = {
              type: 'danger',
              message: 'Something went wrong!',
            };

            if (err.error.error_code === 'invalid_data') {
              this.alert = {
                type: 'danger',
                message: err.error.error_message ?? 'An unexpected error occurred.',
              };
            }

            if (err.error.error_code === 'weak_password') {
              this.alert = {
                type: 'danger',
                message: err.error.error_message ?? 'Your password is too simple. Please use a mix of uppercase and lowercase letters, numbers, and special characters.'
              };
            }
          } else if (err instanceof HttpErrorResponse && err.status === 409) {
            // User already registered: Redirect to log in form
            this.alert = {
              type: 'success',
              message: `Success! You'll get redirected in a few seconds!`,
            };

            setTimeout(() => {
              this._router.navigate(['/login'], {
                queryParams: { email: this._emailAddress },
              });
            }, 2000);
          } else {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        },
      )
      .add(() => {
        this.loading = false;
      });
  }

  public closeAlert() {
    this.alert = null;
  }

  // endregion
}
